import { trpc } from '@/App'
import { Button, buttonVariants } from '@/components/shadcn/button'
import showToastNotification from '@/hooks/useShowToast'
import useUserPermissions from '@/hooks/useUserPermissions'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { Edit, Minus, Plus, Send } from '@mynaui/icons-react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocalStorage } from 'usehooks-ts'
import { z } from 'zod'
import { WorkOSUser } from './UserProfileSettingDetail'
import { Loader } from './custom-components/Loader'
import { Avatar, AvatarFallback } from './shadcn/avatar'
import { Input } from './shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './shadcn/select'
import { Separator } from './shadcn/separator'

const inviteSchema = z.object({
  emails: z.array(z.string().email('Invalid email')).min(1, 'Enter at least one valid email'),
  role: z.enum(['creator', 'commenter']),
})

type InviteFormData = z.infer<typeof inviteSchema>

export const TeamsSection = ({
  setPurchaseTeamSeatOpen,
  newSeatCount,
  setNewSeatCount,
}: {
  setPurchaseTeamSeatOpen: (open: boolean) => void
  newSeatCount: number
  setNewSeatCount: React.Dispatch<React.SetStateAction<number>>
}) => {
  const { userTeams, isFetchingUserTeams } = useUserPermissions()
  const [user] = useLocalStorage<WorkOSUser | undefined>('_wos_user', undefined)
  const utils = trpc.useUtils()
  const { data: teamMembers, isLoading: isLoadingTeamMembers } = trpc.getTeamMembers.useQuery()
  const { mutate: editTeam, isPending: isEditingTeam } = trpc.editTeam.useMutation({
    onSuccess: async () => {
      showToastNotification('success', {
        message: `Success`,
        description: `Team name updated successfully!`,
      })
      setEditTeamNameMode(false)
      await utils.getUserTeams.invalidate()
    },
    onError: () => {
      showToastNotification('error', {
        message: `Failed to invite users`,
        description: `Unfortunately, we couldn't update the team name. Please try again later.`,
      })
      setEditedTeamName(userTeams?.find((team) => team.id == user?.teamId)?.name || '')
      setEditTeamNameMode(false)
    },
  })
  const { mutate: inviteUsers, isPending } = trpc.inviteUsers.useMutation({
    onSuccess: async () => {
      showToastNotification('success', {
        message: `Success`,
        description: `Invites sent successfully!`,
      })

      setEmails([])
      reset()
    },
    onError: () => {
      showToastNotification('error', {
        message: `Failed to invite users`,
        description: `Unfortunately, we couldn't send the invites. Please try again later.`,
      })
    },
  })
  const { mutate: changeTeamMemberRole, isPending: isChangingMemberRole } = trpc.changeTeamMemberRole.useMutation({
    onSuccess: async () => {
      showToastNotification('success', {
        message: `Success`,
        description: `Invites sent successfully!`,
      })
      await utils.getTeamMembers.invalidate()
    },
    onError: () => {
      showToastNotification('error', {
        message: `Failed to invite users`,
        description: `Unfortunately, we couldn't send the invites. Please try again later.`,
      })
    },
  })

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      emails: [],
      role: 'commenter',
    },
  })

  const role = watch('role')

  const [inputEmail, setInputEmail] = useState('')
  const [emails, setEmails] = useState<string[]>([])

  // State for editing team name
  const [editTeamNameMode, setEditTeamNameMode] = useState(false)
  const [editedTeamName, setEditedTeamName] = useState(userTeams?.find((team) => team.id == user?.teamId)?.name || '')

  const addEmail = () => {
    const email = inputEmail.trim()
    if (email && !emails.includes(email)) {
      try {
        inviteSchema.shape.emails.element.parse(email) // Validate email format
        const updatedEmails = [...emails, email]
        setEmails(updatedEmails)
        setValue('emails', updatedEmails, { shouldValidate: true }) // ✅ Sync with RHF
      } catch (err) {
        console.error('Invalid email:', email, err)
      }
    }
    setInputEmail('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', ' ', ','].includes(e.key)) {
      e.preventDefault()
      addEmail()
    }
  }

  const onSubmit = (data: InviteFormData) => {
    const payload = emails.map((email) => ({
      email,
      role: data.role,
    }))
    inviteUsers(payload)
  }

  const currentUserCanEditTeamStuff = ['owner', 'creator'].includes(
    teamMembers?.find((member) => member.id == user?.id)?.role ?? '',
  )

  return (
    <div className="space-y-6">
      {isFetchingUserTeams || isLoadingTeamMembers ? (
        <div className="w-full h-full">
          {' '}
          <Loader />
        </div>
      ) : userTeams && userTeams.length > 0 ? (
        <div className="space-y-6">
          {/* Team header */}
          <div className="flex justify-between gap-3">
            <div className="flex items-center gap-2">
              {editTeamNameMode ? (
                <>
                  <Input
                    className="font-bold text-xl border rounded px-2 py-1"
                    value={editedTeamName}
                    onChange={(e) => setEditedTeamName(e.target.value)}
                    autoFocus
                    disabled={isEditingTeam}
                  />
                  <Button
                    variant="ghost"
                    disabled={isEditingTeam}
                    loading={isEditingTeam}
                    size="icon"
                    onClick={() => {
                      if (
                        editedTeamName.trim() &&
                        editedTeamName !== userTeams.find((team) => team.id == user?.teamId)?.name
                      ) {
                        editTeam({
                          name: editedTeamName.trim(),
                        })
                      }
                    }}
                  >
                    {/* Checkmark icon */}
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={isEditingTeam}
                    size="icon"
                    onClick={() => {
                      setEditTeamNameMode(false)
                      setEditedTeamName(userTeams.find((team) => team.id == user?.teamId)?.name || '')
                    }}
                  >
                    {/* X icon */}
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                      <path
                        d="M18 6L6 18M6 6l12 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Button>
                </>
              ) : (
                <>
                  <span className="font-bold text-xl">
                    {editedTeamName == '' ? userTeams.find((team) => team.id == user?.teamId)?.name : editedTeamName}
                  </span>
                  {currentUserCanEditTeamStuff ? (
                    <Button variant="ghost" size="icon" onClick={() => setEditTeamNameMode(true)}>
                      <Edit size={18} />
                    </Button>
                  ) : null}
                </>
              )}
            </div>
            {/* <Button size="sm" variant="secondary">
              <span>Copy Link</span>
              <Link size="16" />
            </Button> */}
          </div>

          {/* Invite form */}
          {currentUserCanEditTeamStuff ? (
            <div>
              <h5>Invite by email</h5>
              <form onSubmit={handleSubmit(onSubmit)} className="flex justify-between gap-4 mt-4">
                <div className="rounded-lg border border-themeborder flex-1 flex items-center justify-between">
                  <div
                    className="flex items-center gap-2 p-2 w-full max-w-48 overflow-x-auto"
                    ref={(el) => {
                      if (el && emails.length > 0) {
                        el.scrollLeft = el.scrollWidth
                      }
                    }}
                  >
                    {emails.map((email) => (
                      <div
                        key={email}
                        className="flex items-center bg-purple-100 text-purple-800 rounded-full px-3 pb-0.5 text-sm"
                      >
                        {email}
                        <button
                          type="button"
                          disabled={isPending}
                          className="ml-2 text-purple-500 hover:text-purple-700"
                          onClick={() => {
                            const updated = emails.filter((e) => e !== email)
                            setEmails(updated)
                            setValue('emails', updated, { shouldValidate: true }) // ✅ Sync with RHF
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <input
                      type="text"
                      disabled={isPending}
                      value={inputEmail}
                      onChange={(e) => setInputEmail(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type email and press space/enter"
                      className="flex-1 outline-none bg-transparent text-sm"
                    />
                  </div>
                  <Select
                    value={role}
                    disabled={isPending}
                    onValueChange={(val) => setValue('role', val as 'creator' | 'commenter')}
                  >
                    <SelectTrigger
                      className={cn(
                        buttonVariants({ variant: 'ghost', size: 'sm' }),
                        'border-none shadow-none focus-visible:ring-0 w-fit hover:bg-transparent',
                      )}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        { label: 'Creator', value: 'creator' },
                        { label: 'Commenter', value: 'commenter' },
                      ].map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Button disabled={isPending} loading={isPending} type="submit" variant="destructivePurple">
                    <span>Send Invite</span> <Send size={18} />
                  </Button>
                </div>
              </form>
              {/* Error Display */}
              {errors.emails && <p className="text-red-500 text-xs mt-2">{errors.emails.message}</p>}
            </div>
          ) : null}

          {/* Seat controls */}
          {currentUserCanEditTeamStuff ? (
            <div>
              <h3 className="font-bold text-sm">Add or Remove Seats</h3>
              <p className="text-[#FF855F] mt-0.5 text-xs">
                (5/5) &#8226; All free seats have been used - $10/mo for each additional user, or remove users
              </p>
              <div className="flex justify-between gap-5 mt-3">
                <div className="border rounded flex justify-between items-center space-x-2 bg-gray-50 px-2.5 py-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-full bg-white w-[1.75rem] h-[1.75rem] flex justify-center items-center"
                    onClick={() => setNewSeatCount((prev) => Math.max(1, prev - 1))}
                  >
                    <Minus size={16} />
                  </Button>
                  <div className="text-sm">
                    <span className="font-semibold">
                      {newSeatCount} Seat{newSeatCount > 1 ? 's' : ''}
                    </span>{' '}
                    - <span>${newSeatCount * 10}/mo</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-full bg-white w-[1.75rem] h-[1.75rem] flex justify-center items-center"
                    onClick={() => setNewSeatCount((prev) => prev + 1)}
                  >
                    <Plus size={16} />
                  </Button>
                </div>
                <Button onClick={() => setPurchaseTeamSeatOpen(true)} variant="destructivePurple">
                  Purchase Seat
                </Button>
              </div>
            </div>
          ) : null}

          {currentUserCanEditTeamStuff ? <Separator className="my-3" /> : null}

          {/* Members list */}
          <div>
            <h5 className="text-sm font-medium">Members List:</h5>
            <div className="space-y-6 mt-6">
              {(teamMembers ?? []).map((teamMember, index) => (
                <div key={index} className="flex justify-between items-center gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      {teamMember?.avatar ? (
                        <img
                          src={teamMember.avatar}
                          alt={teamMember.firstName || teamMember.email}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <AvatarFallback>
                          {teamMember?.firstName?.[0]}
                          {teamMember?.lastName?.[0]}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div>
                      <p className="font-medium">
                        {`${
                          teamMember?.firstName || teamMember?.lastName
                            ? `${teamMember.firstName ?? ''} ${teamMember.lastName ?? ''}`
                            : teamMember?.email
                        }`.trim()}
                      </p>
                    </div>
                  </div>
                  <div>
                    {teamMember.role == 'owner' ? (
                      <div
                        className={cn(
                          buttonVariants({
                            variant: 'secondary',
                          }),
                        )}
                      >
                        Owner
                      </div>
                    ) : teamMember.id != user?.id && currentUserCanEditTeamStuff ? (
                      <Select
                        disabled={isChangingMemberRole}
                        value={teamMember.role}
                        onValueChange={(value: 'creator' | 'commenter') => {
                          changeTeamMemberRole({
                            memberId: teamMember.memberId,
                            newRole: value,
                          })
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            buttonVariants({
                              variant: 'destructivePurple',
                            }),
                          )}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { label: 'Creator', value: 'creator' },
                            {
                              label: 'Commenter',
                              value: 'commenter',
                            },
                          ].map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div>You have no teams</div>
      )}
    </div>
  )
}
